import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createApiCall } from 'services'
import { CertificateViewAccess } from '@repo/types'
import type { ICertificateView, TCertificateViewVisibility } from '@repo/types'

/**
 * VERA-298 — the certificate's shareable views (the public singleton plus any
 * custom links). Owner-only; the API 404s for anyone else.
 */
export type CertificateView = ICertificateView & { url: string }

export const certificateViewsKey = (certificateId: string) => ['certificate-views', certificateId]

const getCertificateViews = async (certificateId: string): Promise<CertificateView[]> => {
  const response = await createApiCall({
    url: `/certificates/customer/${certificateId}/views`,
    throwError: true,
  })

  return response.data as CertificateView[]
}

export const useCertificateViews = (certificateId?: string, enabled = true) =>
  useQuery({
    queryKey: certificateViewsKey(certificateId as string),
    queryFn: () => getCertificateViews(certificateId as string),
    // Owner-only endpoint. Callers gate on ownership so a public scanner never fires it.
    enabled: Boolean(certificateId) && enabled,
  })

export type UpdateCertificateViewPatch = {
  name?: string
  is_enabled?: boolean
  visibility?: TCertificateViewVisibility
  /**
   * VERA-300 — who may open the link. Inviting someone flips a view to `invited`
   * implicitly; this is the way back, without which removing the last viewer left the
   * link restricted to nobody and unrecoverable from the UI.
   */
  access?: CertificateViewAccess
}

export const useUpdateCertificateView = (certificateId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ viewId, patch }: { viewId: string; patch: UpdateCertificateViewPatch }) => {
      const response = await createApiCall({
        method: 'PATCH',
        url: `/certificates/customer/${certificateId}/views/${viewId}`,
        data: patch,
        throwError: true,
      })

      return response.data as CertificateView
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: certificateViewsKey(certificateId) })
      // The certificate payload itself is visibility-dependent server-side, so it has
      // to be refetched too or the page keeps rendering the pre-toggle shape.
      queryClient.invalidateQueries({ queryKey: ['certificate-public', certificateId] })
    },
  })
}

/** VERA-300 — create a custom share link. */
export const useCreateCertificateView = (certificateId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: { name: string; visibility?: TCertificateViewVisibility }) => {
      const response = await createApiCall({
        method: 'POST',
        url: `/certificates/customer/${certificateId}/views`,
        data: payload,
        throwError: true,
      })

      return response.data as CertificateView
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: certificateViewsKey(certificateId) })
    },
  })
}

export const useDeleteCertificateView = (certificateId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (viewId: string) => {
      await createApiCall({
        method: 'DELETE',
        url: `/certificates/customer/${certificateId}/views/${viewId}`,
        throwError: true,
      })

      return viewId
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: certificateViewsKey(certificateId) })
    },
  })
}

export type CertificateViewViewer = {
  id: string
  view_id: string
  email: string
  invited_at: string
  accepted_at: string | null
  /** Refreshed on every open; `null` means the invitee has never opened the link. */
  last_viewed_at: string | null
}

/**
 * The outcome of an invite. Access-granted and invite-delivered are separate facts:
 * the viewer row is written before the email is attempted, so a mail outage grants
 * access silently. `email_failures` is what lets the UI say so.
 */
export interface InviteViewersResult {
  viewers: CertificateViewViewer[]
  /** Emails newly added by this request. Re-inviting an existing viewer is a no-op. */
  invited: string[]
  /** Subset of `invited` whose email could not be delivered. */
  email_failures: string[]
}

export const viewViewersKey = (certificateId: string, viewId: string) => [
  'certificate-view-viewers',
  certificateId,
  viewId,
]

export const useCertificateViewViewers = (certificateId?: string, viewId?: string) =>
  useQuery({
    queryKey: viewViewersKey(certificateId as string, viewId as string),
    queryFn: async () => {
      const response = await createApiCall({
        url: `/certificates/customer/${certificateId}/views/${viewId}/viewers`,
        throwError: true,
      })

      return response.data as CertificateViewViewer[]
    },
    enabled: Boolean(certificateId && viewId),
  })

/**
 * Invite to a view whose id is only known at call time.
 *
 * `useInviteCertificateViewers` binds `viewId` when the hook is constructed, which
 * cannot work for a view created moments earlier in the same interaction. This takes
 * the id with the payload instead. Same endpoint, same result shape.
 */
export const useInviteViewersToView = (certificateId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ viewId, emails }: { viewId: string; emails: string[] }) => {
      const response = await createApiCall({
        method: 'POST',
        url: `/certificates/customer/${certificateId}/views/${viewId}/viewers`,
        data: { emails },
        throwError: true,
      })

      return response.data as InviteViewersResult
    },
    onSuccess: (_data, { viewId }) => {
      queryClient.invalidateQueries({ queryKey: viewViewersKey(certificateId, viewId) })
      queryClient.invalidateQueries({ queryKey: certificateViewsKey(certificateId) })
    },
  })
}

export const useInviteCertificateViewers = (certificateId: string, viewId?: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (emails: string[]) => {
      const response = await createApiCall({
        method: 'POST',
        url: `/certificates/customer/${certificateId}/views/${viewId}/viewers`,
        data: { emails },
        throwError: true,
      })

      return response.data as InviteViewersResult
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: viewViewersKey(certificateId, viewId as string),
      })
    },
  })
}

export const useRemoveCertificateViewer = (certificateId: string, viewId?: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (viewerId: string) => {
      await createApiCall({
        method: 'DELETE',
        url: `/certificates/customer/${certificateId}/views/${viewId}/viewers/${viewerId}`,
        throwError: true,
      })

      return viewerId
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: viewViewersKey(certificateId, viewId as string),
      })
    },
  })
}

/**
 * The design tells the owner they can separate addresses "by comma, space, or new
 * line", so the parsing has to actually accept all three.
 */
export const parseInviteEmails = (raw: string): string[] =>
  raw
    .split(/[\s,;]+/)
    .map(value => value.trim())
    .filter(Boolean)
